import { type Attribute, Extension, getStylesMap } from '@tiptap/core'
import { TextStyle } from '@tiptap/extension-text-style'
import hyphenateStyleName from 'hyphenate-style-name'
import { TmTextColorCommandsExtension } from '@/services/forms/tmTiptap/extensions/tmTextColorCommandsExtension'
import { TmFontSizeCommandsExtension } from '@/services/forms/tmTiptap/extensions/tmFontSizeCommandsExtension'
import { TmFontFamilyCommandsExtension } from '@/services/forms/tmTiptap/extensions/tmFontFamilyCommandsExtension'

export type TmTiptapAttributesKeeperOptions = {
  forNodes: string[]
  attributes: string[]
  styleRules: (keyof CSSStyleDeclaration)[]
}

const DEFAULT_PRESERVED_HTML_ATTRIBUTES = [
  'align',
  'alt',
  'background',
  'bgcolor',
  'border',
  'cellpadding',
  'cellspacing',
  'color',
  'dir',
  'face',
  'height',
  'hspace',
  'id',
  'lang',
  'name',
  'nowrap',
  'size',
  'style',
  'title',
  'valign',
  'vspace',
  'width',
  'class',
]

const DEFAULT_PRESERVED_STYLE_ATTRIBUTES: Array<keyof CSSStyleDeclaration> = [
  'background',
  'backgroundColor',
  'backgroundImage',
  'backgroundRepeat',
  'backgroundPosition',
  'border',
  'borderCollapse',
  'borderColor',
  'borderSpacing',
  'borderStyle',
  'borderWidth',
  'color',
  'direction',
  'display',
  'font',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'height',
  'letterSpacing',
  'lineHeight',
  'margin',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginTop',
  'padding',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'textAlign',
  'textDecoration',
  'textIndent',
  'textTransform',
  'verticalAlign',
  'visibility',
  'whiteSpace',
  'width',
  'wordSpacing',
]

export const getStyleAttrName = (ruleName: keyof CSSStyleDeclaration): string => `style-${String(ruleName)}`

/**
 * Keep HTML attributes for editor nodes for correct HTML templates pasting
 */
export const TmTiptapHtmlStylesHandlerExtension = Extension.create<TmTiptapAttributesKeeperOptions>({
  name: 'TmTiptapHtmlStylesHandler',
  priority: 9999, // should work before other extensions to avoid conflicts
  addOptions() {
    return {
      forNodes: ['textStyle', 'paragraph', 'table', 'tableHeader', 'tableRow', 'tableCell', 'link', 'image', 'heading'],
      attributes: DEFAULT_PRESERVED_HTML_ATTRIBUTES,
      styleRules: DEFAULT_PRESERVED_STYLE_ATTRIBUTES,
    } satisfies TmTiptapAttributesKeeperOptions
  },
  addGlobalAttributes() {
    const preservedHtmlAttrs = this.options.attributes.reduce<Record<string, undefined>>((result, attrName) => {
      result[attrName] = undefined
      return result
    }, {})

    const preservedStyleAttrs = this.options.styleRules.reduce<Record<string, Attribute>>((result, styleRuleName) => {
      const attrName = getStyleAttrName(styleRuleName)
      const hypenatedAttrName = hyphenateStyleName(styleRuleName as string)
      result[attrName] = {
        default: null,
        parseHTML: (element) => {
          const elementStyleAttribute = element.getAttribute('style')
          const stylesMap = getStylesMap(elementStyleAttribute || '')

          return stylesMap.get(hypenatedAttrName) ?? undefined
        },
        renderHTML: (attributes) => {
          if (!attributes[attrName]) {
            return {}
          }
          return { style: `${hypenatedAttrName}: ${attributes[attrName]}` }
        },
      }
      return result
    }, {})

    return [
      {
        types: this.options.forNodes,
        attributes: {
          ...preservedHtmlAttrs,
          ...preservedStyleAttrs,
        },
      },
    ]
  },
  addExtensions() {
    return [TextStyle, TmTextColorCommandsExtension, TmFontSizeCommandsExtension, TmFontFamilyCommandsExtension]
  },
})
